/**
 * Which queue a resolved park should send the curator back to.
 *
 * `resolveHitl` navigates on success and defaults to `tina-hitl`, which is right for
 * every scrape reason and wrong for the two families that have surfaces of their own.
 * Both were the same bug, found twice:
 *
 * - **enrichment** (`enrichment_low_confidence`) lives on `/tina/enrichment`, and
 *   `EnrichmentResolution` already passes `navigateTo: "tina-enrichment"`. The
 *   force-close path in `hitl-panel.tsx` had its own copy of that branch.
 * - **refresh** (`refresh_review`) lives on `/tina/refresh-review` by owner ruling R55,
 *   and nothing passed anything: resolving one bounced the curator to `/tina/hitl`, a
 *   queue that deliberately hides refresh rows, so the item they had just cleared was
 *   not there and neither was their place in the list.
 *
 * One function so a third surface cannot land with a third copy of the branch — and so
 * the two shapes `navigateTo` accepts stay documented in one place:
 *
 * - a **Refine resource name** (`"tina-enrichment"`) → that resource's bare `list` route.
 *   Enough for the enrichment queue, whose narrowing is all localStorage-backed.
 * - a **router path** (`"/tina/refresh-review?current=3"`) → navigated verbatim. Needed
 *   for the refresh queue, whose page and sorters live in the URL because the app runs
 *   Refine with `syncWithLocation: true`.
 *
 * `undefined` means "the shared queue is right" — the caller then passes no `navigateTo`
 * at all and `resolveHitl`'s own default applies.
 */
import { refreshReviewReturnHref } from "@/pages/tina/refresh-review/lib/refresh-review-return";
import type { HitlContext } from "@/types/tina";

/** The Refine resource name for `/tina/enrichment`. */
export const ENRICHMENT_RESOURCE = "tina-enrichment";

export function hitlQueueReturnTo(
  context: HitlContext | null | undefined,
): string | undefined {
  switch (context?.problem.reason) {
    case "refresh_review":
      // Read at call time, not at render: the memory is written by the queue on its way
      // out, and a panel mounted from a deep link has none — in which case this is the
      // bare route.
      return refreshReviewReturnHref();
    case "enrichment_low_confidence":
    // Same surface as `enrichment_low_confidence` — a curator-review park lives on its
    // own `enrichment_artist_curation` job, never in the shared queue.
    case "enrichment_curator_review":
      return ENRICHMENT_RESOURCE;
    default:
      return undefined;
  }
}
